import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import { GridComponent, TableMode } from 'src/app/components/grid/grid.component';
import { FieldDataType } from 'src/app/components/grid/FieldDataType';
import { QueryOperand } from 'src/app/components/grid/QueryOperand';
import { ITableData } from 'src/app/components/grid/ITableData';
import { IColumn } from 'src/app/components/grid/IColumn';
import { GridUtil } from 'src/app/components/grid/GridUtil';
import { IExam, IExamQuestionBank } from 'src/app/interface/dto';
import { EventService } from 'src/app/services/event-services/error-event.service';
import { ExamService } from 'src/app/services/exam-service.service';

@Component({
  selector: 'app-examination-question-banks',
  templateUrl: './examination-question-banks.component.html',
  styleUrls: ['./examination-question-banks.component.scss']
})
export class ExaminationQuestionBanksComponent implements OnInit, OnDestroy {
  selectedQuestionBanks: IExamQuestionBank[] = [];
  exam: IExam = null;
  examID: number;
  gridName = 'examinationQuestionBanksGrid';
  modelError = '';
  selectedQuestionBank: IExamQuestionBank = null;

  @ViewChild('gridComponent') gridComponent!: GridComponent;

  data: ITableData = {
    tableRows: [],
    tableParams: {
      filterParams: [
        {
          fieldName: 'examinationID',
          fieldValue: `${this.examService.selectedExamIds[0]}`,
          queryOperand: QueryOperand['=='],
          static: true
        },
      ],
      sortParam: {
        fieldName: "",
        orderDirection: 0
      },
      pageInfo: {
        previousPageSize: 50,
        page: 1,
        pageSize: 50,
        recordCount: 0,
        pageCount: 0
      },
      tableMode: TableMode.serverProcessing,
      selectAll: false
    }
  };

  columns: IColumn[] = [
    {
      displayName: 'Question Bank',
      fieldName: 'name',
      fieldDataType: FieldDataType.text,
      searchable: true,
      orderable: true,
    },
    {
      displayName: 'Category',
      fieldName: 'category',
      fieldDataType: FieldDataType.text,
      searchable: true,
      orderable: true,
    },
    {
      displayName: 'Number Of Questions',
      fieldName: 'numberOfQuestions',
      fieldDataType: FieldDataType.number,
      searchable: true,
      orderable: true,
    },
    {
      displayName: 'Question Bank Pool Size',
      fieldName: 'questionBankPoolSize',
      fieldDataType: FieldDataType.number,
      searchable: true,
      orderable: true,
    },
    {
      displayName: 'Question Bank Source',
      fieldName: 'questionBankSource',
      fieldDataType: FieldDataType.text,
      searchable: true,
      orderable: true,
    },
    {
      displayName: 'Disabled',
      fieldName: 'disabled',
      fieldDataType: FieldDataType.boolean,
      searchable: true,
      orderable: true,
    }
  ];

  private readonly reloadGrid$ = new Subject<void>();

  constructor(
    private examService: ExamService,
    private actRoute: ActivatedRoute,
    private cdRef: ChangeDetectorRef,
    private eventService: EventService
  ) { }

  async ngOnInit() {
    GridUtil.setClearGridSelection();

    const queryParams = await firstValueFrom(this.actRoute.params);
    this.examID = queryParams['examid'];
    if (!this.examService.selectedExamIds[0]) {
      this.data.tableParams.filterParams = [
        {
          fieldName: 'examinationID',
          fieldValue: this.examID.toString(),
          queryOperand: QueryOperand['=='],
          static: true
        }
      ]
    }

    this.examService.setSelectedExamQuestionBank([]);

    if (this.examService.selectedExams?.length === 1) {
      this.exam = this.examService.selectedExams[0];
    }

    this.examService.reloadGrid
      .pipe(takeUntil(this.reloadGrid$))
      .subscribe(() => this.refreshData(this.data.tableParams));

    await this.refreshData(this.data.tableParams);
  }

  ngOnDestroy(): void {
    this.reloadGrid$.next();
    this.reloadGrid$.complete();
  }

  updateGridParams(res: any) {
    if (!res) {
      return;
    }
    this.data.tableRows = [];
    const tableParams = { ...this.data.tableParams, pageInfo: res[0].pageInfo };
    this.data.tableParams = tableParams;
    this.data.tableRows = res.slice(1, res.length);
    setTimeout(() => {
      this.eventService.gridDataLoadedEvent.next(true);
    });
  }

  async refreshData(tableParams) {
    tableParams.ExaminationID = this.exam?.id;
    const res = await this.examService.getExaminationQuestionBanks(tableParams) as any[];
    this.updateGridParams(res);
  }

  emitQuestionBankData(selectedIds: any[]) {
    if (!this.data?.tableRows) {
      setTimeout(() => this.emitQuestionBankData(selectedIds), 50);
      return;
    }

    const questionBankList: any[] = [];
    selectedIds.forEach(x => questionBankList.push(this.data.tableRows[x]));
    this.selectedQuestionBanks = questionBankList;
    this.selectedQuestionBank = this.selectedQuestionBanks[0];
    this.examService.setSelectedExamQuestionBank([...this.selectedQuestionBanks]);

    let storedIds = GridUtil.getSelectedDataID(this.gridName) ?? [];
    let selectedDataRowsId = storedIds.length > 0 ? storedIds : questionBankList.map(x => x.id);
    GridUtil.setRowCliclk(this.gridName, selectedIds, selectedDataRowsId);
  }

  addQuestionBank() {
    this.gridComponent.resetRowSelected();
    this.selectedQuestionBank = {
      examinationID: this.exam.id,
      questionBankID: 0,
      category: '',
      numberOfQuestions: 0,
      questionBankPoolSize: 0,
      questionBankSource: '',
      disabled: false,
      totalNumberOfQuestions: 0,
      id: 0,
      name: "",
      isWebTestBank: false,
      isPsychometricTestBank: false
    };
    this.cdRef.detectChanges();
  }
}
